// IndexedDB storage for the sync engine: records by "kind/id", meta by key. A cache written from the truth plus the
// queue of unsent writes; nothing here decides anything (kit:Decision/core-owns-no-io).
import type { Storage, Stored, StorageWrite, StoredRecord } from "./types";

const req = <T>(r: IDBRequest<T>) =>
	new Promise<T>((ok, fail) => {
		r.onsuccess = () => ok(r.result);
		r.onerror = () => fail(r.error);
	});

const done = (tx: IDBTransaction) =>
	new Promise<void>((ok, fail) => {
		tx.oncomplete = () => ok();
		tx.onerror = () => fail(tx.error);
		tx.onabort = () => fail(tx.error);
	});

/** One database per instance and person, e.g. `indexedDb("hq-kit:michael:usr_x")`. */
export function indexedDb(name: string): Storage {
	let db: Promise<IDBDatabase> | null = null;
	const open = () =>
		(db ??= new Promise((ok, fail) => {
			const r = indexedDB.open(name, 1);
			r.onupgradeneeded = () => {
				r.result.createObjectStore("records");
				r.result.createObjectStore("meta");
			};
			r.onsuccess = () => ok(r.result);
			r.onerror = () => fail(r.error);
		}));
	// Writes in order: a later batch never lands before an earlier one.
	let chain = Promise.resolve();
	return {
		async load(): Promise<Stored> {
			const d = await open();
			const tx = d.transaction(["records", "meta"], "readonly");
			const records = (await req(tx.objectStore("records").getAll())) as StoredRecord[];
			const keys = (await req(tx.objectStore("meta").getAllKeys())) as string[];
			const values = await req(tx.objectStore("meta").getAll());
			const meta: Record<string, unknown> = {};
			keys.forEach((k, i) => (meta[k] = values[i]));
			return { records, meta };
		},
		apply(writes: StorageWrite[]) {
			chain = chain.then(async () => {
				const d = await open();
				const tx = d.transaction(["records", "meta"], "readwrite");
				const records = tx.objectStore("records");
				const meta = tx.objectStore("meta");
				for (const w of writes) {
					if (w.op === "put") records.put(w.record, `${w.record.kind}/${w.record.id}`);
					else if (w.op === "delete") records.delete(`${w.kind}/${w.id}`);
					else if (w.op === "meta") meta.put(w.value, w.key);
					else {
						records.clear();
						meta.clear();
					}
				}
				await done(tx);
			});
			return chain;
		},
	};
}

/** Storage in memory: tests, and hosts that keep nothing. */
export function memory(): Storage & { stored: Stored } {
	const stored: Stored = { records: [], meta: {} };
	const byKey = new Map<string, StoredRecord>();
	return {
		stored,
		async load() {
			return { records: [...byKey.values()], meta: { ...stored.meta } };
		},
		async apply(writes) {
			for (const w of writes) {
				if (w.op === "put") byKey.set(`${w.record.kind}/${w.record.id}`, w.record);
				else if (w.op === "delete") byKey.delete(`${w.kind}/${w.id}`);
				else if (w.op === "meta") stored.meta[w.key] = w.value;
				else {
					byKey.clear();
					stored.meta = {};
				}
			}
			stored.records = [...byKey.values()];
		},
	};
}
